import React from 'react';
import { Box, Typography } from '@mui/material';
import { SurveyType } from '../../../../../shared/constants';
import { surveyScoreToLabel } from '../../../../../shared/surveys';
import {
  dimGrey,
  inactive,
  lightBlack,
  lightPurple,
} from '../../../../../mainTheme';
import type { SurveyInsightsDistributionEntry } from '../../../../../store/hubOwner/types';
import SurveyResponseChip from '../OverviewTab/SurveyResponseChip';

interface SurveyDistributionProps {
  distribution: SurveyInsightsDistributionEntry[];
  surveyType: SurveyType;
}

// How often each score was given in the window: one row per score on the
// scale (best at the top), a proportional bar and the count / share.
const SurveyDistribution: React.FC<SurveyDistributionProps> = ({
  distribution,
  surveyType,
}) => {
  const rows = [...distribution].sort((a, b) => b.score - a.score);
  const maxCount = Math.max(0, ...rows.map((r) => Number(r.count) || 0));

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      data-testid="survey-distribution"
    >
      {rows.map((entry) => {
        const count = Number(entry.count) || 0;
        const label =
          entry.label ?? surveyScoreToLabel(surveyType, entry.score) ?? 'N/A';
        const width = maxCount ? (count / maxCount) * 100 : 0;
        return (
          <Box
            key={entry.score}
            display="flex"
            alignItems="center"
            gap={1.5}
            data-testid={`distribution-row-${entry.score}`}
          >
            <Box sx={{ width: 150, flexShrink: 0 }}>
              <SurveyResponseChip response={label} isCurrent={count > 0} />
            </Box>
            <Box
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: inactive,
                overflow: 'hidden',
              }}
              aria-hidden
            >
              <Box
                sx={{
                  width: `${width}%`,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: lightPurple,
                  transition: 'width 0.3s',
                }}
              />
            </Box>
            <Typography
              sx={{
                width: 72,
                flexShrink: 0,
                textAlign: 'right',
                color: count > 0 ? lightBlack : dimGrey,
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px',
              }}
            >
              {count}
              <Typography
                component="span"
                sx={{
                  color: dimGrey,
                  fontSize: '12px',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                ({entry.percentage ?? 0}%)
              </Typography>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SurveyDistribution;
